import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { chatApi } from '@/api/chatApi';
import { useChatStore } from '@/store/chatStore';
import { useAuthStore } from '@/store/authStore';
import { getSocket } from '@/lib/socket';
import { QK } from './queryKeys';


/** Lấy danh sách hội thoại */
export function useConversations() {
  const setConversations = useChatStore((s) => s.setConversations);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const query = useQuery({
    queryKey: QK.CONVERSATIONS,
    queryFn: chatApi.getConversations,
    enabled: isAuthenticated,
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  useEffect(() => {
    if (query.data && Array.isArray(query.data)) {
      setConversations(query.data);
    }
  }, [query.data, setConversations]);

  return query;
}

/** Lấy tin nhắn theo conversationId */
export function useMessages(conversationId: string | null) {
  const setMessages = useChatStore((s) => s.setMessages);

  const query = useQuery({
    queryKey: QK.MESSAGES(conversationId ?? ''),
    queryFn: () => chatApi.getMessages(conversationId!),
    enabled: !!conversationId,
    staleTime: 1000 * 30,
    retry: false,
  });

  useEffect(() => {
    if (query.data && Array.isArray(query.data) && conversationId) {
      setMessages(conversationId, query.data);
    }
  }, [query.data, conversationId, setMessages]);

  return query;
}

/** Gửi tin nhắn (REST) */
export function useSendMessage() {
  const addMessage = useChatStore((s) => s.addMessage);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: chatApi.sendMessage,
    onSuccess: (msg) => {
      addMessage(msg.conversationId, msg);
      qc.invalidateQueries({ queryKey: QK.CONVERSATIONS });
    },
  });
}

/** Tạo hội thoại mới */
export function useCreateConversation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: chatApi.createConversation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.CONVERSATIONS });
    },
  });
}

/** Cập nhật hội thoại (title, isArchived) */
export function useUpdateConversation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, data }: { conversationId: string; data: { title?: string; isArchived?: boolean } }) => 
      chatApi.updateConversation(conversationId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.CONVERSATIONS });
    },
  });
}

/** Đánh dấu hội thoại đã đọc */
export function useMarkAsRead() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: chatApi.markAsRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.CONVERSATIONS });
    },
  });
}

/** Xóa hội thoại */
export function useDeleteConversation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: chatApi.deleteConversation,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.CONVERSATIONS });
    },
  });
}

/**
 * Hook lắng nghe tin nhắn real-time qua socket.
 * Flow đơn giản: register (SocketProvider) + lắng nghe `newMessage` → refetch data.
 * Gọi 1 lần trong ChatPage.
 */
export function useChatSocket() {
  const qc = useQueryClient();
  const activeConversationId = useChatStore((s) => s.activeConversationId);

  useEffect(() => {
    try {
      const socket = getSocket();

      const handler = (data: { conversationId: string }) => {
        console.log('[Socket] 📩 newMessage:', data);

        // Refetch danh sách conversations (cập nhật lastMessage, thứ tự)
        qc.invalidateQueries({ queryKey: QK.CONVERSATIONS });

        // Nếu đang mở đúng conversation đó → refetch messages
        if (data.conversationId && data.conversationId === activeConversationId) {
          qc.invalidateQueries({ queryKey: QK.MESSAGES(data.conversationId) });
        }
      };

      socket.on('newMessage', handler);
      console.log('[Socket] ✅ Listening for newMessage');

      return () => {
        socket.off('newMessage', handler);
      };
    } catch {
      // Socket not available
    }
  }, [qc, activeConversationId]);
}
